/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';

import {PagePermissionType} from './models/page-permission-type';

import {LoginComponent} from './components/login.component';
import {MultiFactorAuthenticationComponent} from './components/multifactor-authentication.component';
import {PageNotFoundComponent} from './components/page-not-found.component';
import {HomeComponent} from './components/home.component';
import {UserComponent} from './components/users/user.component';
import {PeopleComponent} from './components/infrastructure/people.component';
import {InfrastructureComponent} from './components/infrastructure/infrastructure.component';
import {AssetsComponent} from './components/infrastructure/assets.component';
import {ServerObjectsComponent} from './components/infrastructure/server-objects.component';
import {DevicesComponent} from './components/infrastructure/devices.component';
import {DriverObjectsComponent} from './components/infrastructure/driver-objects.component';
import {ServicesComponent} from './components/infrastructure/services.component';
import {ReadersComponent} from './components/infrastructure/readers.component';
import {AntennasComponent} from './components/infrastructure/antennas.component';
import {TagsComponent} from './components/infrastructure/tags.component';
import {LocationsComponent} from './components/infrastructure/locations.component';
import {ZonesComponent} from './components/infrastructure/zones.component';
import {SettingsComponent} from './components/settings/settings.component';
import {SecurityComponent} from './components/security/security.component';
import {LicensingComponent} from "./components/licensing/licensing.component";
import {LocateComponent} from "./components/locate/locate.component";
import {MainAssetsComponent} from "./components/main-assets.component";
import {MainPeopleComponent} from "./components/main-people.component";
import {SitesComponent} from "./components/sites/sites.component";
import {ActionsComponent} from './components/actions.component';
import {EventsComponent} from './components/events.component';
import {CanActivateAuthGuard} from './can-activate.authguard';
import {RoleManagerComponent} from "./components/roleManager/rolemanager.component";
import {HistoryComponent} from "./components/history/history.component";
import { RuleEditorComponent } from "./components/rules/rule-editor.component";
import { RulesComponent } from "./components/rules/rules.component";
import {ShellComponent} from "./components/shell.component";
import { ActionsResolver } from "./resolvers/actions.resolver";
import {AntennaInstancesResolver} from "./resolvers/antenna-instances.resolver";
import {AntennaInstancesPagingResolver} from "./resolvers/antenna-instances-paging.resolver";
import {AntennasResolver} from "./resolvers/antennas.resolver";
import {AntennasPagingResolver} from "./resolvers/antennas-paging.resolver";
import {AssetInstancesPagingResolver} from "./resolvers/asset-instances-paging.resolver";
import {PeopleInstancesPagingResolver} from "./resolvers/people-instances-paging.resolver";
import {AssetsPagingResolver} from "./resolvers/assets-paging.resolver";
import {PeoplePagingResolver} from "./resolvers/people-paging.resolver";
import {DeviceInstancesResolver} from "./resolvers/device-instances.resolver";
import {DeviceInstancesPagingResolver} from "./resolvers/device-instances-paging.resolver";
import {DevicesResolver} from "./resolvers/devices.resolver";
import {DevicesPagingResolver} from "./resolvers/devices-paging.resolver";
import { DialogsResolver } from "./resolvers/dialogs.resolver";
import {DriverObjectInstancesResolver} from "./resolvers/driver-object-instances.resolver";
import {DriverObjectInstancesPagingResolver} from "./resolvers/driver-object-instances-paging.resolver";
import {DriverObjectsResolver} from "./resolvers/driver-objects.resolver";
import {DriverObjectsPagingResolver} from "./resolvers/driver-objects-paging.resolver";
import { EventsResolver } from "./resolvers/events.resolver";
import {LocationInstancesResolver} from "./resolvers/location-instances.resolver";
import {LocationInstancesPagingResolver} from "./resolvers/location-instances-paging.resolver";
import {LocationsResolver} from "./resolvers/locations.resolver";
import {LocationsPagingResolver} from "./resolvers/locations-paging.resolver";
import { LocationZoneInstancesResolver } from "./resolvers/location-zone-instances.resolver";
import {ReaderInstancesResolver} from "./resolvers/reader-instances.resolver";
import {ReaderInstancesPagingResolver} from "./resolvers/reader-instances-paging.resolver";
import {ReadersResolver} from "./resolvers/readers.resolver";
import {ReadersPagingResolver} from "./resolvers/readers-paging.resolver";
import { RolesResolver } from "./resolvers/roles.resolver";
import { RuleEditorResolver } from "./resolvers/rule-editor.resolver";
import { RulesResolver } from "./resolvers/rules.resolver";
import { RulesPagingResolver } from "./resolvers/rules-paging.resolver";
import {ServerObjectInstancesResolver} from "./resolvers/server-object-instances.resolver";
import {ServerObjectInstancesPagingResolver} from "./resolvers/server-object-instances-paging.resolver";
import {ServerObjectsResolver} from "./resolvers/server-objects.resolver";
import {ServerObjectsPagingResolver} from "./resolvers/server-objects-paging.resolver";
import {ServiceInstancesResolver} from "./resolvers/service-instances.resolver";
import {ServiceInstancesPagingResolver} from "./resolvers/service-instances-paging.resolver";
import {ServicesResolver} from "./resolvers/services.resolver";
import {ServicesPagingResolver} from "./resolvers/services-paging.resolver";
import {TagDefinitionsResolver} from "./resolvers/tag-definitions.resolver";
import {TagDefinitionsPagingResolver} from "./resolvers/tag-definitions-paging.resolver";
import {TagInstancesResolver} from "./resolvers/tag-instances.resolver";
import {TagInstancesPagingResolver} from "./resolvers/tag-instances-paging.resolver";
import { UsersPagingResolver } from "./resolvers/users-paging.resolver";
import {ZoneInstancesResolver} from "./resolvers/zone-instances.resolver";
import {ZoneInstancesPagingResolver} from "./resolvers/zone-instances-paging.resolver";
import {ZonesResolver} from "./resolvers/zones.resolver";
import {ZonesPagingResolver} from "./resolvers/zones-paging.resolver";
import {CanDeactivateGuard} from "./services/can-deactivate-guard.service";
import {CustomFieldsComponent} from "./components/customFields/customFields.component";
import {LicenseNotValidComponent} from "./components/license-not-valid.component";
import { LicensesResolver } from './resolvers/licenses.resolver';
import { CompiledLicenseResolver } from './resolvers/compiled-license.resolver';
import { LicensedHardwaresResolver } from './resolvers/licensed-hardwares.resolver';
import { RfidEncodingSchemesComponent } from './components/infrastructure/rfid-encoding-schemes.component';
import {PeopleInstancesResolver} from "./resolvers/people-instances.resolver";
import { WorkflowsComponent } from './components/workflow/workflows.component';
import { WorkflowComponent } from './components/workflow/workflow.component';
import { WorkflowResolver } from "./resolvers/workflow.resolver";
import { HealthMonitorComponent } from './components/healthMonitor/health-monitor.component';
import {RfidEncodingSchemesPagingResolver} from "./resolvers/rifd-encoding-schemes-paging.resolver";
import {RfidEncodingSchemeInstancesPagingResolver} from "./resolvers/rfid-encoding-scheme-instances-paging.resolver";
import {PeopleObjectParametersResolver} from "./resolvers/peopleObjectParametersResolver";
import {LocationParametersResolver} from "./resolvers/locationParametersResolver";
import { AssetPropertiesMainDisplayResolver } from "app/resolvers/asset-properties-main-display.resolver";
import { MonitorZoneComponent, MonitorZone_ResultsPerPage } from './components/locate/monitor-zone/monitor-zone.component';
import { NewMapOptionsResolver } from './resolvers/new-map-options.resolver';
import { PeoplePropertiesMainDisplayResolver } from './resolvers/people-properties-main-display.resolver';
import { MonitorAssetsComponent, MonitorAssets_AssetsPerPage } from 'app/components/locate/monitor-assets/monitor-assets.component';
import { MonitorLocationComponent, MonitorLocation_ResultsPerPage } from './components/locate/monitor-location/monitor-location.component';
import { KissPrintPageComponent } from './components/kiss/print/kiss-print-page.component';
import { MonitorTagsComponent, MonitorTags_TagsPerPage } from './components/locate/monitor-tags/monitor-tags.component';
import { TagPropertiesMainDisplayResolver } from './resolvers/tag-properties-main-display.resolver';
import { KissPortalValidationEnterInfoComponent } from './components/kiss/portal_validation/enter-info/kiss-portal-validation.enter-info.component';
import { KissPortalValidationSelectPortalComponent } from './components/kiss/portal_validation/select-portal/kiss-portal-validation.select-portal.component';
import { ServiceInstancesOfTypeResolver } from './resolvers/service-instances-of-type.resolver';
import { KissOutboundServiceTypeUuid, KissPortalServiceTypeUuid, KissService } from './services/kiss.service';
import { KissPortalValidationStateResolver } from './resolvers/kiss-portal-validation.state.resolver';
import { KissPortalValidationDashboardComponent } from './components/kiss/portal_validation/dashboard/kiss-portal-validation.dashboard.component';
import { KissReaderStateResolver } from './resolvers/kiss.reader-state.resolver';
import { UsersMmcInfoComponent, UsersMmcInfoComponent_PageSize } from './components/mmc/users-mmc-info.component';
import { UserMmcInfoResolver } from './resolvers/user-mmc-info.resolver';
import { MmcAffiliatesResolver } from './resolvers/mmc-affiliates.resolver';
import { DemoOrderValidationSelectPortalComponent } from './components/demo-order-validation/select-portal/demo-order-validation.select-portal.component';
import { DemoOrderValidation_PortalType_Uuid } from './services/demo-order-validation.service';
import { ServiceInstanceResolver } from './resolvers/service-instance.resolver';
import { DemoOrderValidationEnterInfoComponent } from './components/demo-order-validation/enter-info/demo-order-validation.enter-info.component';
import { DemoOrderValidationDashboardComponent } from './components/demo-order-validation/dashboard/demo-order-validation.dashboard.component';
import { DemoOrderValidationStateResolver } from './resolvers/demo-order-validation.state.resolver';
import { DenverMattressPortalValidationSelectPortalComponent } from './components/denverMattress/portal_validation/select-portal/dm-portal-validation.select-portal.component';
import { DenverMattressPortalValidationDashboardComponent } from './components/denverMattress/portal_validation/dashboard/dm-portal-validation.dashboard.component';
import { DenverMattressPortalServiceTypeUuid, DenverMattressReaderStateEnum } from './services/denver-mattress.service';
import { DenverMattressPortalValidationStateResolver } from './resolvers/denver-mattress-portal-validation.state.resolvers';
import { HHSeekAndFindPageComponent } from './components/hhseekandfind/page/hhseekandfind-page.component';
import { BuchananProductUploadComponent } from './components/buchanan/product-upload/buchanan.product-upload.component';
import { BuchananBulkPrintComponent } from './components/buchanan/bulk-print/buchanan.bulk-print.component';
import { BuchananPrintComponent } from './components/buchanan/print/buchanan.print.component';
import { OneilPortalValidationSelectPortalComponent } from './components/oneil/select-portal/oneil-portal-validation.select-portal.component';
import { Oneil_PortalServiceType_Uuid } from './services/oneill.service';
import { OneillPortalValidationStateResolver } from './resolvers/oneill-portal-validation-state.resolver';
import { OneilPortalValidationDashboardComponent } from './components/oneil/dashboard/oneil-portal-validation.dashboard.component';
import { DialogsComponent } from './components/dialogs/dialogs.component'; 
import { DialogsPagingResolver } from './resolvers/dialogs-paging.resolver';
import { GtinValidationSetComponent } from "./components/gtin-validation-set/gtin-validation-set.component";
import { GtinValidationSetPagingResolver } from './resolvers/gtin-validation-set-paging.resolver';
import { ApiTokenComponent} from "./components/apiToken/apiToken.component";
import { ApiTokenPagingResolver } from './resolvers/apitoken.resolver';
import { CartonAuditSelectLocationComponent } from './components/jockey/carton-audit/select-location/carton-audit-select-location.component';
import { CartonAudit_ServiceType_Uuid } from './services/carton-audit.service';
import { CartonAuditEnterInfoComponent } from './components/jockey/carton-audit/enter-info/carton-audit-enter-info.component';
import { CartonAuditDashboardComponent } from "./components/jockey/carton-audit/dashboard/carton-audit-dashboard.component";
import { CartonAuditStateResolver } from './resolvers/carton-audit-state.resolver';
import { AbleNetShipmentValidationEnterInfoComponent } from './components/ablenet/shipment-validation-portal/enter-info/ablenet.shipment-validation.enter-info.component';
import { AbleNetShipmentValidationDashboardComponent } from './components/ablenet/shipment-validation-portal/dashboard/ablenet.shipment-validation.dashboard.component';
import { AbleNetShipmentValidationSelectPortalComponent } from './components/ablenet/shipment-validation-portal/select-portal/ablenet.shipment-validation.select-portal.component';
import { AbleNetShipmentValidation_Type_Uuid } from './services/ablenet.service';
import { AbleNetValidationStateResolver } from './resolvers/ablenet.resolver';
import { AbleNetReturnDevicesSelectPortalComponent } from './components/ablenet/return-devices-portal/select-portal/ablenet.return-devices.select-portal.component';
import { DataMappingHdrComponent } from "./components/dataMapping/data-mapping-hdr.component";
import { DataMappingHdrResolver } from "./resolvers/data-mapping-hdr.resolver";
import { DataMappingUploadComponent } from "./components/dataMapping/data-mapping-upload.component";
import { KissOutboundValidationSelectPortalComponent } from './components/kiss/outbound/select-portal/kiss-outbound-validation.select-portal.component';
import { KissOutboundValidationEnterInfoComponent } from './components/kiss/outbound/enter-info/kiss-outbound-validation.enter-info.component';
import { KissOutboundValidationDashboardComponent } from './components/kiss/outbound/dashboard/kiss-outbound-validation.dashboard.component';
import { KissOutboundStateResolver } from './resolvers/kiss-outbound-state.resolver';
import { AbleNetReturnDevicesDashboardComponent } from './components/ablenet/return-devices-portal/dashboard/ablenet.return-devices.dashboard.component';
import { AbleNetReturnDevicesStateResolver } from './resolvers/ablenet-return-devices.resolver';
import { AbleNetReturnDevicesValidation_Type_Uuid } from './services/ablenet.returndevices.service';

const routes: Routes = [
  {
    path: '',
    component: ShellComponent,
    children: [
      {
        path: 'home',
        component: HomeComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.HomePage}
      },
      {
        path: 'users',
        component: UserComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.UsersPage},
        resolve: {users: UsersPagingResolver, peopleInstances: PeopleInstancesResolver}
      },
      {
        path: 'users/mmc-info',
        component: UsersMmcInfoComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.SetMmcAffiliatesPage,
          userMmcsPerPage: UsersMmcInfoComponent_PageSize
        },
        resolve: { 
          userMmcInfo: UserMmcInfoResolver,
          mmcAffiliates: MmcAffiliatesResolver
        }
      },
      {
        path: 'customFields',
        component: CustomFieldsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.CustomFieldsPage}
      },
      {
        path: 'healthMonitor',
        component: HealthMonitorComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.HealthMonitorPage}
      },
      {
        path: 'infrastructure',
        component: InfrastructureComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructurePage}
      },
      {
        path: 'infrastructure/efassets',
        component: AssetsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureAssetsPage},
        resolve: {assets: AssetsPagingResolver, assetInstances: AssetInstancesPagingResolver}
      },
      {
        path: 'infrastructure/people',
        component: PeopleComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructurePeoplePage},
        resolve: {people: PeoplePagingResolver, peopleInstances: PeopleInstancesPagingResolver}
      },
      {
        path: 'infrastructure/server-objects',
        component: ServerObjectsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureServerObjectsPage},
        resolve: {serverObjects: ServerObjectsPagingResolver, serverObjectInstances: ServerObjectInstancesPagingResolver}
      },
      {
        path: 'infrastructure/devices',
        component: DevicesComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureDevicesPage},
        resolve: {devices: DevicesPagingResolver, deviceInstances: DeviceInstancesPagingResolver }
      },
      {
        path: 'infrastructure/rfid-encoding-schemes',
        component: RfidEncodingSchemesComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.InfrastructureRfidEncodingSchemesPage
        },
        resolve: {
          rfidEncodingSchemes: RfidEncodingSchemesPagingResolver, 
          rfidEncodingSchemeInstances: RfidEncodingSchemeInstancesPagingResolver
        }
      },
      {
        path: 'infrastructure/driver-objects',
        component: DriverObjectsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureDriverObjectsPage},
        resolve: {driverObjects: DriverObjectsPagingResolver, driverObjectInstances: DriverObjectInstancesPagingResolver }
      },
      {
        path: 'infrastructure/services',
        component: ServicesComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureServicesPage},
        resolve: {services: ServicesPagingResolver, serviceInstances: ServiceInstancesPagingResolver}
      },
      {
        path: 'infrastructure/readers',
        component: ReadersComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureReadersPage},
        resolve: {readers: ReadersPagingResolver, readerInstances: ReaderInstancesPagingResolver}
      },
      {
        path: 'infrastructure/antennas',
        component: AntennasComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureAntennasPage},
        resolve: {antennas: AntennasPagingResolver, antennaInstances: AntennaInstancesPagingResolver}
      },
      {
        path: 'infrastructure/tags',
        component: TagsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureTagsPage},
        resolve: {tagDefinitions: TagDefinitionsPagingResolver, tagInstances: TagInstancesPagingResolver}
      },
      {
        path: 'infrastructure/locations',
        component: LocationsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureLocationsPage},
        resolve: {locations: LocationsPagingResolver, locationInstances: LocationInstancesPagingResolver}
      },
      {
        path: 'infrastructure/zones',
        component: ZonesComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.InfrastructureZonesPage},
        resolve: {zones: ZonesPagingResolver, zoneInstances: ZoneInstancesPagingResolver}
      },
       {
        path: 'infrastructure/data-mapping-sets',
        component: DataMappingHdrComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.DataMappingPage},
        resolve: {headers: DataMappingHdrResolver}
      },
      {
        path: 'efassets',
        component: MainAssetsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.AssetsPage},
        resolve: {assets: AssetsPagingResolver, assetInstances: AssetInstancesPagingResolver}
      },
      {
        path: 'people',
        component: MainPeopleComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.PeoplePage},
        resolve: {people: PeoplePagingResolver, peopleInstances: PeopleInstancesPagingResolver}
      },
      {
        path: 'settings',
        component: SettingsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.SettingsPage}
      },
      {
        path: 'licensing',
        component: LicensingComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.LicensingPage},
        resolve: { availableHardware: LicensedHardwaresResolver, compiledLicense: CompiledLicenseResolver, licenses: LicensesResolver }
      },
      {
        path: 'locate',
        component: LocateComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.LocatePage},
        resolve: { assetPropertiesForMainDisplay: AssetPropertiesMainDisplayResolver, peopleObjectParameters: PeopleObjectParametersResolver,
                   locationParameters: LocationParametersResolver}
      },
      {
        path: 'monitor-location',
        component: MonitorLocationComponent,
        canActivate: [CanActivateAuthGuard],
        data: { 
          pagePermissionType: PagePermissionType.MonitorLocationPage, 
          locationsPerPage: MonitorLocation_ResultsPerPage 
        },
        resolve: { 
          initialLocations: LocationInstancesPagingResolver,
          assetSearchProperties: AssetPropertiesMainDisplayResolver,
          peopleSearchProperties: PeoplePropertiesMainDisplayResolver
        }
      },
      {
        path: 'monitor-zone',
        component: MonitorZoneComponent,
        canActivate: [CanActivateAuthGuard],
        data: { 
          pagePermissionType: PagePermissionType.MonitorZonePage, 
          zonesPerPage: MonitorZone_ResultsPerPage 
        },
        resolve: { 
          initialZones: ZoneInstancesPagingResolver,
          assetSearchProperties: AssetPropertiesMainDisplayResolver,
          peopleSearchProperties: PeoplePropertiesMainDisplayResolver
        }
      },
      {
        path: 'monitor-assets',
        component: MonitorAssetsComponent,
        canActivate: [CanActivateAuthGuard],
        data: { 
          pagePermissionType: PagePermissionType.MonitorAssetsPage,
          assetsPerPage: MonitorAssets_AssetsPerPage
        },
        resolve: { 
          initialAssets: AssetInstancesPagingResolver,
          assetSearchProperties: AssetPropertiesMainDisplayResolver
        }
      },
      {
        path: 'monitor-tags',
        component: MonitorTagsComponent,
        canActivate: [CanActivateAuthGuard],
        data: { 
          pagePermissionType: PagePermissionType.MonitorTagsPage,
          tagsPerPage: MonitorTags_TagsPerPage
        },
        resolve: { 
          initialTags: TagInstancesPagingResolver,
          initialAssets: AssetInstancesPagingResolver,
          tagSearchProperties: TagPropertiesMainDisplayResolver
        }
      },
      {
        path: 'security',
        component: SecurityComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.SecurityPage}
      },
      {
        path: 'sites',
        component: SitesComponent,
        canActivate: [CanActivateAuthGuard],
        canDeactivate: [CanDeactivateGuard],
        data: {pagePermissionType: PagePermissionType.SitesPage}
      },
      {
        path: 'roleManager',
        component: RoleManagerComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.RoleManagerPage}
      },
      {
        path: 'reactions',
        component: ActionsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.ReactionsPage}
      },
      {
        path: 'triggers',
        component: EventsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.TriggersPage}
      },
      {
        path: 'history',
        component: HistoryComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.HistoryPage}
      },
      {
        path: 'rules',
        component: RulesComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.RulesPage},
        resolve: {rules: RulesPagingResolver}
      },
      {
        path: 'rules/editor',
        component: RuleEditorComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.RulesPage},
        resolve: {rule: RuleEditorResolver, events: EventsResolver, actions: ActionsResolver, dialogs: DialogsResolver, roles: RolesResolver, locations: LocationZoneInstancesResolver}
      },
      {
        path: 'rules/editor/:id',
        component: RuleEditorComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.RulesPage},
        resolve: {rule: RuleEditorResolver, events: EventsResolver, actions: ActionsResolver, dialogs: DialogsResolver, roles: RolesResolver, locations: LocationZoneInstancesResolver}
      },
      {
        path: 'dialogs',
        component: DialogsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.RulesPage},
        resolve: {dialogs: DialogsPagingResolver}
      },
      {
        path: 'workflows',
        component: WorkflowsComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.WorkflowsPage}
      },
      {
        path: 'workflow/editor',
        component: WorkflowComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.WorkflowsPage}
      },
      {
        path: 'workflow/editor/:id',
        component: WorkflowComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.WorkflowsPage},
        resolve: {workflow: WorkflowResolver}
      },
      {
        path: 'kiss/print',
        component: KissPrintPageComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.KissPrintPage}
      },
      {
        path: 'kiss/portal-validation/select-portal',
        component: KissPortalValidationSelectPortalComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.KissPortalValidationPage,
          serviceTypeUuid: KissPortalServiceTypeUuid
        },
        resolve: {
          availablePortals: ServiceInstancesOfTypeResolver
        }
      },
      {
        path: 'kiss/portal-validation/:id/enter-info',
        component: KissPortalValidationEnterInfoComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.KissPortalValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver
        }
      },
      {
        path: 'kiss/portal-validation/:id/dashboard',
        component: KissPortalValidationDashboardComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.KissPortalValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver,
          state: KissPortalValidationStateResolver,
          readerState: KissReaderStateResolver
        }
      },
      {
        path: 'kiss/outbound-validation/select-portal',
        component: KissOutboundValidationSelectPortalComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.KissOutboundValidationPage,
          serviceTypeUuid: KissOutboundServiceTypeUuid
        },
        resolve: {
          availablePortals: ServiceInstancesOfTypeResolver
        }
      },
      {
        path: 'kiss/outbound-validation/:id/enter-info',
        component: KissOutboundValidationEnterInfoComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.KissOutboundValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver
        }
      },
      {
        path: 'kiss/outbound-validation/:id/dashboard',
        component: KissOutboundValidationDashboardComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.KissOutboundValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver,
          currentState: KissOutboundStateResolver
        }
      },
      // END: KISS
      // START: Denver Mattress
      {
        path: 'denver-mattress/portal-validation/select-portal',
        component: DenverMattressPortalValidationSelectPortalComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.DenverMattressPortalValidationPage,
          serviceTypeUuid: DenverMattressPortalServiceTypeUuid
        },
        resolve: {
          availablePortals: ServiceInstancesOfTypeResolver
        }
      },
      {
        path: 'denver-mattress/portal-validation/:id/dashboard',
        component: DenverMattressPortalValidationDashboardComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.DenverMattressPortalValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver,
          validationInfo: DenverMattressPortalValidationStateResolver,
        }
      },
      // END: Denver Mattress

      // START: Demo Portal Validation
      {
        path: 'order/portal-validation/select-portal',
        component: DemoOrderValidationSelectPortalComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.DemoOrderPortalValidationPage,
          serviceTypeUuid: DemoOrderValidation_PortalType_Uuid
        },
        resolve: {
          availablePortals: ServiceInstancesOfTypeResolver
        }
      },
      {
        path: 'order/portal-validation/:id/enter-info',
        component: DemoOrderValidationEnterInfoComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.DemoOrderPortalValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver
        }
      },
      {
        path: 'order/portal-validation/:id/dashboard',
        component: DemoOrderValidationDashboardComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.DemoOrderPortalValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver,
          state: DemoOrderValidationStateResolver
        }
      },
      // END: Demo Portal Validation

      // START: Buchanan
      {
        path: 'hh/seek-and-find-operations',
        component: HHSeekAndFindPageComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.HomePage
        },
        resolve: { }
      },
      {
        path: 'buchanan/upload-product',
        component: BuchananProductUploadComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.BuchananUploadProductPage,
        },
      },
      {
        path: 'buchanan/bulk-print',
        component: BuchananBulkPrintComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.BuchananBulkPrintPage,
        },
      },
      {
        path: 'buchanan/print',
        component: BuchananPrintComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.BuchananPrintPage,
        },
      },
      // END: Buchanan

      // START: O'Neil
      {
        path: 'oneill/carton-portal-validation/select-portal',
        component: OneilPortalValidationSelectPortalComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.ONeilCartonPortalPages,
          serviceTypeUuid: Oneil_PortalServiceType_Uuid
        },
        resolve: {
          availablePortals: ServiceInstancesOfTypeResolver
        }
      },
      {
        path: 'oneill/carton-portal-validation/:id/dashboard',
        component: OneilPortalValidationDashboardComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.ONeilCartonPortalPages
        },
        resolve: {
          portal: ServiceInstanceResolver,
          state: OneillPortalValidationStateResolver
        }
      },
      // END: O'Neil
      {
        path: 'gtin-validation-sets',
        component: GtinValidationSetComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.GtinValidationSetsPage},
        resolve: {gtinvalidationsetheader: GtinValidationSetPagingResolver}
      },
      {
        path: 'api-tokens',
        component: ApiTokenComponent,
        canActivate: [CanActivateAuthGuard],
        data: {pagePermissionType: PagePermissionType.ApiTokenPage},
        resolve: {tokenHeaders: ApiTokenPagingResolver}
      },
      {
        path: 'jockey/carton-audit/select-location',
        component: CartonAuditSelectLocationComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.CartonAuditPortalPage,
          serviceTypeUuid: CartonAudit_ServiceType_Uuid
        },
        resolve: {
          portal: ServiceInstanceResolver,
          availableServices: ServiceInstancesOfTypeResolver
        }
      },
      {
        path: 'jockey/carton-audit/:id/enter-info',
        component: CartonAuditEnterInfoComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.CartonAuditPortalPage
        },
        resolve: {
          portal: ServiceInstanceResolver
        }
      },
      {
        path: 'jockey/carton-audit/:id/dashboard',
        component: CartonAuditDashboardComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.CartonAuditPortalPage
        },
        resolve: {
          portal: ServiceInstanceResolver,
          auditState: CartonAuditStateResolver
        }
      },
      // START: AbleNet Shipment Validation
      {
        path: 'ablenet/shipment-validation/select-service',
        component: AbleNetShipmentValidationSelectPortalComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.AbleNetShipmentValidationPage,
          serviceTypeUuid: AbleNetShipmentValidation_Type_Uuid
        },
        resolve: {
          portal: ServiceInstanceResolver,
          availablePortals: ServiceInstancesOfTypeResolver
        }
      },
      {
        path: 'ablenet/shipment-validation/:id/enter-info',
        component: AbleNetShipmentValidationEnterInfoComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.AbleNetShipmentValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver,
        }
      },
      {
        path: 'ablenet/shipment-validation/:id/dashboard',
        component: AbleNetShipmentValidationDashboardComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.AbleNetShipmentValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver,
          state: AbleNetValidationStateResolver
        }
      },
      // END : AbleNet Shipment Validation
      // START: AbleNet Return Devices Validation
      {
        path: 'ablenet/return-devices/select-service',
        component: AbleNetReturnDevicesSelectPortalComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.AbleNetReturnDevicesValidationPage,
          serviceTypeUuid: AbleNetReturnDevicesValidation_Type_Uuid,
        },
        resolve: {
          portal: ServiceInstanceResolver,
          availablePortals: ServiceInstancesOfTypeResolver
        }
      },
      {
        path: 'ablenet/return-devices/:id/dashboard',
        component: AbleNetReturnDevicesDashboardComponent,
        canActivate: [CanActivateAuthGuard],
        data: {
          pagePermissionType: PagePermissionType.AbleNetReturnDevicesValidationPage
        },
        resolve: {
          portal: ServiceInstanceResolver,
          state: AbleNetReturnDevicesStateResolver
        }
      },
      // END : AbleNet Return Devices Validation
      {
        path: 'data-mapping-sets',
        component: DataMappingHdrComponent,
        canActivate: [CanActivateAuthGuard],
        resolve: {
          headers: DataMappingHdrResolver
        },
        data: {
          pagePermissionType: PagePermissionType.DataMappingPage
        }
      },
      {
        path: 'data-mapping-sets/uploadfile',
        component: DataMappingUploadComponent,
        canActivate: [CanActivateAuthGuard],

        data: {
          pagePermissionType: PagePermissionType.DataMappingPageUpload
        }
      },

      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'login/mfa',
    component: MultiFactorAuthenticationComponent
  },
  { path: 'invalidLicense',
    component: LicenseNotValidComponent
  },
  {
    path: '**',
    component: PageNotFoundComponent
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule],
  providers: [
    ActionsResolver,
    AntennasResolver,
    AntennasPagingResolver,
    AntennaInstancesResolver,
    AntennaInstancesPagingResolver,
    AssetsPagingResolver,
    AssetInstancesPagingResolver,
    PeoplePagingResolver,
    PeopleInstancesPagingResolver,
    DialogsResolver,
    DevicesResolver,
    DevicesPagingResolver,
    DeviceInstancesResolver,
    DeviceInstancesPagingResolver,
    DriverObjectsResolver,
    DriverObjectsPagingResolver,
    DriverObjectInstancesResolver,
    DriverObjectInstancesPagingResolver,
    EventsResolver,
    LocationsResolver,
    LocationsPagingResolver,
    LocationInstancesResolver,
    LocationInstancesPagingResolver,
    ReadersResolver,
    ReadersPagingResolver,
    ReaderInstancesResolver,
    ReaderInstancesPagingResolver,
    RfidEncodingSchemesPagingResolver,
    RfidEncodingSchemeInstancesPagingResolver,
    RolesResolver,
    RuleEditorResolver,
    RulesResolver,
    RulesPagingResolver,
    ServerObjectsResolver,
    ServerObjectsPagingResolver,
    ServerObjectInstancesResolver,
    ServerObjectInstancesPagingResolver,
    ServicesResolver,
    ServicesPagingResolver,
    ServiceInstancesResolver,
    ServiceInstancesPagingResolver,
    TagDefinitionsResolver,
    TagDefinitionsPagingResolver,
    TagInstancesResolver,
    TagInstancesPagingResolver,
    UsersPagingResolver,
    ZonesResolver,
    ZonesPagingResolver,
    ZoneInstancesResolver,
    ZoneInstancesPagingResolver,
    LocationZoneInstancesResolver,
    LicensesResolver,
    CompiledLicenseResolver,
    LicensedHardwaresResolver,
    PeopleInstancesResolver,
    WorkflowResolver,
    AssetPropertiesMainDisplayResolver,
    PeopleObjectParametersResolver,
    LocationParametersResolver,
    NewMapOptionsResolver,
    PeoplePropertiesMainDisplayResolver,
    ServiceInstancesOfTypeResolver,
    KissPortalValidationStateResolver,
    KissReaderStateResolver,
    UserMmcInfoResolver,
    MmcAffiliatesResolver,
    ServiceInstanceResolver,
    DemoOrderValidationStateResolver,
    DenverMattressPortalValidationStateResolver,
    OneillPortalValidationStateResolver,
    DialogsPagingResolver,
    GtinValidationSetPagingResolver,
    ApiTokenPagingResolver,
    AbleNetValidationStateResolver,
    AbleNetReturnDevicesStateResolver,
    DataMappingHdrResolver

  ]
})
export class AppRoutingModule {

}
